// Compose / submit feedback (handoff 7.2). Summary + detail + attachments.
// Client-side guard before upload: reject >20MB and disallowed types (AC-B-008).
// 429 -> retry message with Retry-After, action disabled (AC-B-007).
// On success -> detail screen of the new feedback.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { AppBar } from '../components/AppBar';
import { BottomActionBar } from '../components/BottomActionBar';
import { AttachmentChip } from '../components/AttachmentChip';
import { Paperclip, Warning } from '../lib/icons';
import { api, ApiError } from '../lib/api';
import { useScreenEnter } from '../lib/motion';

const SUMMARY_MAX = 120;
const DETAIL_MAX = 2000;
const MAX_BYTES = 20 * 1024 * 1024; // 20 MiB (AC-B-008)

const ALLOWED_EXTS = ['.log'];

function isAllowed(file: { name: string; type: string }): boolean {
  const { type, name } = file;
  if (type.startsWith('image/')) return true;
  if (type === 'application/pdf') return true;
  if (type === 'text/plain') return true;
  const lower = name.toLowerCase();
  return ALLOWED_EXTS.some((ext) => lower.endsWith(ext));
}

interface PendingFile {
  key: string;
  name: string;
  size: number;
  type: string;
  blob: Blob;
  state: 'done' | 'rejected';
  reason?: string;
}

let keySeq = 0;

export function Compose() {
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);
  const [summary, setSummary] = useState('');
  const [detail, setDetail] = useState('');
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);

  useEffect(() => {
    if (retryAfter === null) return;
    const t = setTimeout(() => setRetryAfter(null), retryAfter * 1000);
    return () => clearTimeout(t);
  }, [retryAfter]);

  const onPick = (list: FileList | null) => {
    if (!list) return;
    const next: PendingFile[] = [];
    for (const f of Array.from(list)) {
      const rejected = !isAllowed(f);
      next.push({
        key: `pf-${keySeq++}`,
        name: f.name,
        size: f.size,
        type: f.type || 'application/octet-stream',
        blob: f,
        state: rejected || f.size > MAX_BYTES ? 'rejected' : 'done',
        reason: rejected ? '不支持的文件类型' : f.size > MAX_BYTES ? '超过 20MB 限制' : undefined,
      });
    }
    setFiles((prev) => [...prev, ...next]);
    if (fileInput.current) fileInput.current.value = '';
  };

  const removeFile = (key: string) => setFiles((prev) => prev.filter((f) => f.key !== key));

  const summaryOk = summary.trim().length > 0;
  const detailOk = detail.trim().length > 0;
  const canSubmit = summaryOk && detailOk && !submitting && retryAfter === null;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const accepted = files.filter((f) => f.state === 'done');
      const created = await api.createFeedback({
        summary: summary.trim(),
        detail: detail.trim(),
        files: accepted.map((f) => ({ name: f.name, size: f.size, type: f.type, blob: f.blob })),
      });
      navigate(`/${created.id}`, { replace: true });
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 429 && err.retryAfter) {
        setRetryAfter(err.retryAfter);
        setError(`操作过于频繁，请 ${err.retryAfter} 秒后重试`);
      } else {
        setError(err.message || '提交失败，请重试');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AppBar title="提交反馈" onBack={() => navigate('/list')} />
      <main className="app-main">
        <motion.section
          className="section"
          {...useScreenEnter()}
          style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}
        >
          <h1 className="sr-only">提交反馈</h1>

          <div className="field">
            <label htmlFor="summary" className="label">摘要</label>
            <input
              id="summary"
              name="summary"
              autoComplete="off"
              className="input"
              placeholder="一句话描述问题"
              value={summary}
              onChange={(e) => setSummary(e.target.value.slice(0, SUMMARY_MAX))}
              maxLength={SUMMARY_MAX}
              aria-describedby="summary-help summary-count"
            />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span id="summary-help" className="helper">一句话描述问题。</span>
              <span id="summary-count" className="counter">{summary.length}/{SUMMARY_MAX}</span>
            </div>
          </div>

          <div className="field">
            <label htmlFor="detail" className="label">详情</label>
            <textarea
              id="detail"
              name="detail"
              autoComplete="off"
              className="textarea"
              placeholder="发生了什么、预期、实际、复现步骤"
              value={detail}
              onChange={(e) => setDetail(e.target.value.slice(0, DETAIL_MAX))}
              maxLength={DETAIL_MAX}
              aria-describedby="detail-help detail-count"
            />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span id="detail-help" className="helper">越具体越有助于快速定位。</span>
              <span id="detail-count" className="counter">{detail.length}/{DETAIL_MAX}</span>
            </div>
          </div>

          <div className="field">
            <span className="label">附件</span>
            <span className="helper" id="attach-help">支持图片、截图、PDF、日志，单个 ≤20MB。</span>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept="image/*,application/pdf,text/plain,.log"
              className="sr-only"
              onChange={(e) => onPick(e.target.files)}
              aria-describedby="attach-help"
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => fileInput.current?.click()}
            >
              <Paperclip weight="regular" aria-hidden="true" />
              选择文件
            </button>
            {files.length > 0 && (
              <div className="stack-sm">
                {files.map((f) => (
                  <AttachmentChip
                    key={f.key}
                    name={f.name}
                    size={f.size}
                    mime={f.type}
                    state={f.state}
                    reason={f.reason}
                    onRemove={() => removeFile(f.key)}
                  />
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="error-banner" role="alert">
              <Warning weight="regular" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          {/* Desktop inline submit (bottom bar is hidden on md+). */}
          <div className="hidden md:flex md:justify-end">
            <button type="button" className="btn btn-primary" disabled={!canSubmit} onClick={submit}>
              {submitting ? '提交中…' : '提交反馈'}
            </button>
          </div>
        </motion.section>
      </main>

      <BottomActionBar>
        <button type="button" className="btn btn-primary btn-block md:hidden" disabled={!canSubmit} onClick={submit}>
          {submitting ? '提交中…' : '提交反馈'}
        </button>
      </BottomActionBar>
    </>
  );
}
